import anthropicClient from './anthropicClient';

/**
 * Analyzes post content and determines if it matches keyword criteria
 * @param {string} postContent - Content of the post to analyze
 * @param {string[]} targetKeywords - Keywords to match against
 * @param {string} matchType - 'any' or 'all' keywords must match
 * @returns {Promise<Object>} Match analysis result
 */
export async function analyzePostForKeywords(postContent, targetKeywords, matchType = 'any') {
  try {
    const keywordsList = targetKeywords?.join(', ');
    
    const prompt = `Analyze if this post is relevant to these keywords/topics: ${keywordsList}

Post content: "${postContent}"

Match type: ${matchType === 'all' ? 'ALL keywords must be relevant' : 'ANY keyword can be relevant'}

Consider synonyms, related concepts, and context. Respond in JSON format:
{
  "isMatch": <boolean>,
  "matchedKeywords": [<array of matched keywords>],
  "relevanceScore": <number 0-1>,
  "reasoning": "<brief explanation>"
}`;

    const response = await anthropicClient?.post('/messages', {
      model: 'claude-sonnet-4-20250514',
      messages: [
        { 
          role: 'user', 
          content: prompt 
        }
      ],
      max_tokens: 500,
      temperature: 0.3,
    });

    const text = response?.data?.content?.[0]?.text?.trim();
    const jsonMatch = text?.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('Invalid response format from Claude');
    }
    
    return JSON.parse(jsonMatch?.[0]);
  } catch (error) {
    console.error('Error analyzing post for keywords with Claude:', error);
    throw new Error('Failed to analyze post. Please try again.');
  }
}

/**
 * Generates an AI-powered comment for a post
 * @param {Object} options - Comment generation options
 * @param {string} options.postContent - Content of the post to comment on
 * @param {string} options.commentStyle - Style of comment (supportive, insightful, question, humorous)
 * @param {string} options.brandVoice - Brand voice to maintain
 * @param {string[]} options.keywords - Keywords that triggered this comment
 * @param {number} options.maxLength - Maximum comment length
 * @returns {Promise<string>} Generated comment text
 */
export async function generateComment(options) {
  const { 
    postContent, 
    commentStyle = 'supportive', 
    brandVoice = 'professional', 
    keywords = [], 
    maxLength = 200 
  } = options;

  try {
    const keywordContext = keywords?.length > 0 
      ? `This post was identified because it relates to: ${keywords?.join(', ')}.` 
      : '';

    const styleGuide = {
      supportive: 'encouraging and positive',
      insightful: 'thoughtful and adds value',
      question: 'curious and engaging with a question',
      humorous: 'light-hearted and fun while staying professional'
    };

    const prompt = `Generate a ${styleGuide?.[commentStyle]} comment for this social media post.

Post content: "${postContent}"

${keywordContext}

Requirements:
- Brand voice: ${brandVoice}
- Maximum length: ${maxLength} characters
- Be authentic and human-like
- Add value to the conversation
- Avoid generic responses
- DO NOT use hashtags in comments
- Sound natural, not robotic

Generate only the comment text, no explanations or quotes.`;

    const response = await anthropicClient?.post('/messages', {
      model: 'claude-sonnet-4-20250514',
      messages: [
        { 
          role: 'user', 
          content: prompt 
        }
      ],
      max_tokens: 300,
      temperature: 0.7,
    });

    return response?.data?.content?.[0]?.text?.trim();
  } catch (error) {
    console.error('Error generating comment with Claude:', error);
    throw new Error('Failed to generate comment. Please try again.');
  }
}

/**
 * Generates multiple comment variations
 * @param {Object} options - Comment generation options
 * @param {number} variations - Number of variations to generate
 * @returns {Promise<Array>} Array of generated comments
 */
export async function generateCommentVariations(options, variations = 3) {
  try {
    const comments = await Promise.all(
      Array.from({ length: variations }, () => generateComment(options))
    );
    return comments;
  } catch (error) {
    console.error('Error generating comment variations with Claude:', error);
    throw new Error('Failed to generate comment variations. Please try again.');
  }
}

/**
 * Moderates comment content for safety
 * @param {string} comment - Comment text to moderate
 * @returns {Promise<Object>} Moderation results
 */
export async function moderateComment(comment) {
  try {
    const prompt = `Analyze this comment for safety and appropriateness:

"${comment}"

Respond in JSON format:
{
  "isSafe": <boolean>,
  "categories": {
    "hate": <boolean>,
    "harassment": <boolean>,
    "violence": <boolean>,
    "sexual": <boolean>,
    "self-harm": <boolean>
  },
  "flagged": <boolean>
}`;

    const response = await anthropicClient?.post('/messages', {
      model: 'claude-sonnet-4-20250514',
      messages: [
        { 
          role: 'user', 
          content: prompt 
        }
      ],
      max_tokens: 300,
      temperature: 0.2,
    });

    const text = response?.data?.content?.[0]?.text?.trim();
    const jsonMatch = text?.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('Invalid response format from Claude');
    }
    
    const result = JSON.parse(jsonMatch?.[0]);
    
    return {
      isSafe: result?.isSafe,
      categories: result?.categories,
      categoryScores: {}, // Claude doesn't provide scores, only boolean flags
      flagged: result?.flagged
    };
  } catch (error) {
    console.error('Error moderating comment with Claude:', error);
    throw new Error('Failed to moderate comment. Please try again.');
  }
}

/**
 * Analyzes post sentiment and engagement potential
 * @param {string} postContent - Post content to analyze
 * @returns {Promise<Object>} Sentiment and engagement analysis
 */
export async function analyzePostSentiment(postContent) {
  try {
    const prompt = `Analyze this post for sentiment and engagement potential:

"${postContent}"

Respond in JSON format:
{
  "sentiment": "<positive|negative|neutral|mixed>",
  "emotionalTone": [<array of emotions>],
  "engagementPotential": "<high|medium|low>",
  "recommendedAction": "<comment|skip|monitor>",
  "reasoning": "<brief explanation>"
}`;

    const response = await anthropicClient?.post('/messages', {
      model: 'claude-sonnet-4-20250514',
      messages: [
        { 
          role: 'user', 
          content: prompt 
        }
      ],
      max_tokens: 500,
      temperature: 0.3,
    });

    const text = response?.data?.content?.[0]?.text?.trim();
    const jsonMatch = text?.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('Invalid response format from Claude');
    }
    
    return JSON.parse(jsonMatch?.[0]);
  } catch (error) {
    console.error('Error analyzing post sentiment with Claude:', error);
    throw new Error('Failed to analyze post sentiment. Please try again.');
  }
}